/**
 * The studio's language, read out of the session and written back into it.
 *
 * `MCK-20260521-010`, owner decision of 2026-09-12: the studio chooses the language, the
 * choice lives in the database, the browser only supplies the default at creation. Both
 * functions here are pure and both have their own test, because between them they are the
 * whole of the criterion this repo could not previously meet — *"switching the studio's
 * language in workspace settings is immediate, and the choice survives the session"*.
 *
 * **Deliberately no runtime imports.** The unit runner has no DOM (ADR-0015), so a module that
 * pulls in the API client or the locale store cannot be loaded in it. Keeping these two
 * functions free of everything but types is what makes the rule testable at all.
 */

import type { Me } from './queries';

/**
 * The language tag of the studio this tab is acting as, or `null` when nobody has chosen one.
 *
 * `null` covers three real states and they behave identically: no session yet, no active
 * workspace, and a workspace that has not been through B0 — the server suppresses `locale`
 * before the claim exactly as it suppresses the name, so an unclaimed studio cannot hand the
 * reader a server default dressed up as a decision.
 *
 * The membership row rather than `GET /workspace`: the session payload is already fetched by
 * `RequireSession` before any screen below it renders, so the language is known at the first
 * paint instead of one request later. A second fetch would cost every cold load a visible
 * flash of the browser's language.
 */
export function studioLocaleOf(me: Me | null | undefined, workspaceId: string | null): string | null {
  if (me === null || me === undefined || workspaceId === null) return null;
  return me.workspaces.find((workspace) => workspace.id === workspaceId)?.locale ?? null;
}

/**
 * The session, with one studio's language replaced by the one that was just saved.
 *
 * **This is the "immediate" half of the criterion.** `PATCH /workspace` answers with the new
 * representation, so the client already knows the new language the moment the save lands;
 * invalidating the session instead would spend a round trip during which the screen still
 * renders the old language, which is a reload in everything but name. Writing the known answer
 * straight into the cache entry the guard reads makes the switch land in the same commit.
 *
 * TWIN: `WorkspaceMembershipSummary.locale` (backend) and `WorkspaceResponse.locale` are the
 * same column arriving on two payloads (retro rule 12). A save updates the workspace entry —
 * `features/settings/queries.ts` — and this keeps the session entry from disagreeing with it
 * until the next refetch. If either payload stops carrying the column, both sites change.
 *
 * Returns the same object when nothing matched, so a caller can hand the result straight to
 * `setQueryData` without forcing a notification for a workspace that is not in the list.
 */
export function withStudioLocale(me: Me, workspaceId: string, locale: string): Me {
  let changed = false;
  const workspaces = me.workspaces.map((workspace) => {
    if (workspace.id !== workspaceId || workspace.locale === locale) return workspace;
    changed = true;
    return { ...workspace, locale };
  });
  return changed ? { ...me, workspaces } : me;
}
